'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/store/hooks'
import { useImageCrop } from '@/components/image-crop'
import {
    createProductCategory,
    updateProductCategory,
    getProductCategory,
    getCategoriesOnlyByCompany,
    ProductCategoryItem,
    removeProductCategoryImage,
    uploadProductCategoryImage,
} from '@/lib/api/product-category/product-category-api'
import { toast } from 'react-toastify'
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import {
    MdArrowBack,
    MdCategory,
    MdSubdirectoryArrowRight,
    MdStorefront,
    MdImage,
    MdUpload,
    MdDelete,
} from 'react-icons/md'
import {
    StoreConfig,
    getStoreConfigByCompany,
    getStoresByCategory,
    syncCategoryStores,
} from '@/lib/api/commerce/store-config'
import ConfirmModal from '@/components/confirm-modal'

export default function ProductCategoryFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    const isEditing = !!id
    const isSubcategoryMode =
        searchParams.get('tipo') === 'subcategoria'

    const [name, setName] = useState('')
    const [isSubcategory, setIsSubcategory] = useState(
        isSubcategoryMode
    )
    const [parentId, setParentId] = useState<number | null>(
        null
    )
    const [parentCategories, setParentCategories] = useState<
        ProductCategoryItem[]
    >([])
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [image, setImage] = useState<string | null>(null)
    const [uploadingImage, setUploadingImage] = useState(false)
    // Os temas mostram a categoria em círculo, card e menu: o corte é quadrado.
    const { cropFile, cropDialog } = useImageCrop({ aspect: 1, output: { maxWidth: 800 } })

    // Lojas online
    const [availableStores, setAvailableStores] = useState<StoreConfig[]>([])
    const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([])
    const [showNoStoreModal, setShowNoStoreModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadParentCategories()
            loadStores()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (isEditing) {
            loadCategory()
        }
    }, [id])

    const loadParentCategories = async () => {
        if (!user?.companyId) return

        try {
            const response =
                await getCategoriesOnlyByCompany(
                    user.companyId
                )
            if (response.success) {
                setParentCategories(response.result)
            }
        } catch (error) {
            // silently fail
        }
    }

    const loadStores = async () => {
        if (!user?.companyId) return
        try {
            const res = await getStoreConfigByCompany(user.companyId)
            if (res.success && res.result) {
                setAvailableStores(
                    Array.isArray(res.result) ? res.result : [res.result],
                )
            }
        } catch {
            // silenciar erro
        }
    }

    const loadCategoryStores = async (categoryId: number) => {
        try {
            const res = await getStoresByCategory(categoryId)
            if (res.success && res.result) {
                const links = Array.isArray(res.result) ? res.result : [res.result]
                setSelectedStoreIds(links.map((l) => l.storeConfigId))
            }
        } catch {
            // silenciar erro
        }
    }

    const loadCategory = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getProductCategory(
                parseInt(id, 10)
            )
            if (response.success) {
                setName(response.result.name)
                setImage(response.result.image ?? null)
                setIsSubcategory(
                    response.result.isSubcategory
                )
                setParentId(
                    response.result.parentId ?? null
                )
                loadCategoryStores(response.result.id)
            } else {
                toast.error(
                    response.error ||
                    'Erro ao carregar categoria'
                )
                navigate('/registrations/product-categories')
            }
        } catch (error) {
            toast.error('Erro ao carregar categoria')
            navigate('/registrations/product-categories')
        } finally {
            setLoadingData(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        if (isSubcategory && !parentId) {
            toast.error(
                'Selecione a categoria principal para a subcategoria'
            )
            return
        }

        if (!user?.companyId) {
            toast.error('Empresa não identificada')
            return
        }

        // Categoria sem loja não aparece em nenhuma vitrine: avisa antes de
        // criar, mas deixa seguir (pode ser vinculada depois, na edição).
        if (
            !isEditing &&
            availableStores.length > 0 &&
            selectedStoreIds.length === 0
        ) {
            setShowNoStoreModal(true)
            return
        }

        await saveCategory()
    }

    const saveCategory = async () => {
        if (!user?.companyId) return

        setShowNoStoreModal(false)
        setLoading(true)
        try {
            if (isEditing) {
                const response =
                    await updateProductCategory(
                        parseInt(id!, 10),
                        {
                            name: name.trim(),
                            isSubcategory,
                            parentId: isSubcategory
                                ? parentId
                                : null,
                        }
                    )
                if (response.success) {
                    if (user?.companyId) {
                        await syncCategoryStores(
                            parseInt(id!, 10),
                            selectedStoreIds,
                            user.companyId,
                        ).catch(() => {})
                    }
                    toast.success(
                        isSubcategory
                            ? 'Subcategoria atualizada com sucesso'
                            : 'Categoria atualizada com sucesso'
                    )
                    navigate(
                        '/registrations/product-categories'
                    )
                } else {
                    toast.error(
                        response.error ||
                        'Erro ao atualizar categoria'
                    )
                }
            } else {
                const response =
                    await createProductCategory({
                        name: name.trim(),
                        companyId: user.companyId,
                        isSubcategory,
                        parentId: isSubcategory
                            ? parentId!
                            : undefined,
                    })
                if (response.success) {
                    if (response.result?.id && user?.companyId) {
                        await syncCategoryStores(
                            response.result.id,
                            selectedStoreIds,
                            user.companyId,
                        ).catch(() => {})
                    }
                    toast.success(
                        isSubcategory
                            ? 'Subcategoria criada com sucesso'
                            : 'Categoria criada com sucesso'
                    )
                    navigate(
                        '/registrations/product-categories'
                    )
                } else {
                    toast.error(
                        response.error ||
                        'Erro ao criar categoria'
                    )
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar categoria')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando categoria...
                </p>
            </div>
        )
    }

    const title = isSubcategory
        ? isEditing
            ? 'Editar Subcategoria'
            : 'Nova Subcategoria'
        : isEditing
            ? 'Editar Categoria'
            : 'Nova Categoria'

    const subtitle = isSubcategory
        ? isEditing
            ? 'Atualize os dados da subcategoria'
            : 'Cadastre uma nova subcategoria de produto'
        : isEditing
            ? 'Atualize os dados da categoria'
            : 'Cadastre uma nova categoria de produto'

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() =>
                        navigate(
                            '/registrations/product-categories'
                        )
                    }
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        {isSubcategory ? (
                            <MdSubdirectoryArrowRight className="text-2xl text-white" />
                        ) : (
                            <MdCategory className="text-2xl text-white" />
                        )}
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {title}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {subtitle}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex flex-col gap-4 max-w-md">
                        {!isEditing && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={
                                            isSubcategory
                                        }
                                        onChange={e => {
                                            setIsSubcategory(
                                                e.target
                                                    .checked
                                            )
                                            if (
                                                !e.target
                                                    .checked
                                            ) {
                                                setParentId(
                                                    null
                                                )
                                            }
                                        }}
                                    />
                                    <span className="label-text font-medium">
                                        Esta é uma
                                        subcategoria
                                    </span>
                                </label>
                            </div>
                        )}

                        {isSubcategory && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Categoria principal *
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={parentId ?? ''}
                                    onChange={e =>
                                        setParentId(
                                            e.target.value
                                                ? parseInt(
                                                    e
                                                        .target
                                                        .value,
                                                    10
                                                )
                                                : null
                                        )
                                    }
                                    required
                                >
                                    <option value="">
                                        Selecione a
                                        categoria principal
                                    </option>
                                    {parentCategories.map(
                                        cat => (
                                            <option
                                                key={
                                                    cat.id
                                                }
                                                value={
                                                    cat.id
                                                }
                                            >
                                                {cat.name}
                                            </option>
                                        )
                                    )}
                                </select>
                                {parentCategories.length ===
                                    0 && (
                                        <label className="label">
                                            <span className="label-text-alt text-warning">
                                                Nenhuma
                                                categoria
                                                cadastrada.
                                                Crie uma
                                                categoria
                                                primeiro.
                                            </span>
                                        </label>
                                    )}
                            </div>
                        )}

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    {isSubcategory
                                        ? 'Nome da subcategoria *'
                                        : 'Nome da categoria *'}
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder={
                                    isSubcategory
                                        ? 'Ex: Smartphones, Notebooks...'
                                        : 'Ex: Eletrônicos, Alimentos, Serviços...'
                                }
                                className="input input-bordered w-full"
                                value={name}
                                onChange={e =>
                                    setName(e.target.value)
                                }
                                required
                                autoFocus
                            />
                        </div>
                    </div>
                </div>

                {/* Imagem: categorias em círculos, cards de categoria e menu da loja. Vai na
                    hora (não espera o Salvar), como a foto do profissional. */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                    <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4 flex items-center gap-2">
                        <MdImage className="text-secondary" />
                        Imagem na loja
                    </h2>
                    {isEditing ? (
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-base-300 bg-base-200">
                                {image ? (
                                    <img src={image} alt={name} className="h-full w-full object-cover" />
                                ) : (
                                    <MdImage className="text-4xl text-base-content/30" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="text-sm text-base-content/70">
                                    Aparece nos temas que mostram categorias com imagem (círculos, cards e menu).
                                    Quadrada, a partir de 600 x 600 px. PNG, JPG ou WEBP, até 5 MB.
                                </p>
                                <div className="flex gap-2">
                                    <label className={`btn btn-sm btn-outline ${uploadingImage ? 'btn-disabled' : ''}`}>
                                        {uploadingImage ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdUpload />
                                        )}
                                        {image ? 'Trocar imagem' : 'Enviar imagem'}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/gif"
                                            className="hidden"
                                            disabled={uploadingImage}
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0]
                                                e.target.value = ''
                                                if (!file || !id) return
                                                const cropped = await cropFile(file)
                                                if (!cropped) return
                                                setUploadingImage(true)
                                                try {
                                                    const res = await uploadProductCategoryImage(parseInt(id, 10), cropped)
                                                    if (res.success && res.result) {
                                                        setImage(res.result.image ?? null)
                                                        toast.success('Imagem salva')
                                                    } else {
                                                        toast.error(res.error || 'Não foi possível enviar a imagem')
                                                    }
                                                } finally {
                                                    setUploadingImage(false)
                                                }
                                            }}
                                        />
                                    </label>
                                    {image && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-ghost text-error"
                                            disabled={uploadingImage}
                                            onClick={async () => {
                                                if (!id) return
                                                const res = await removeProductCategoryImage(parseInt(id, 10))
                                                if (res.success) setImage(null)
                                                else toast.error(res.error || 'Não foi possível tirar a imagem')
                                            }}
                                        >
                                            <MdDelete /> Tirar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-base-content/60">
                            Salve a {isSubcategory ? 'subcategoria' : 'categoria'} para enviar a imagem.
                        </p>
                    )}
                    {cropDialog}
                </div>

                {/* Lojas Online */}
                {availableStores.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                        <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4 flex items-center gap-2">
                            <MdStorefront className="text-secondary" />
                            Lojas Online
                        </h2>
                        <p className="text-sm text-base-content/60 mb-3">
                            Selecione em quais lojas esta categoria será exibida.
                            Se nenhuma for selecionada, a categoria ficará disponível apenas internamente.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {availableStores.map((store) => (
                                <label
                                    key={store.id}
                                    className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                        selectedStoreIds.includes(store.id)
                                            ? 'border-secondary bg-secondary/5'
                                            : 'border-base-300 hover:border-secondary/50'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary checkbox-sm"
                                        checked={selectedStoreIds.includes(store.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedStoreIds((prev) => [...prev, store.id])
                                            } else {
                                                setSelectedStoreIds((prev) =>
                                                    prev.filter((sid) => sid !== store.id),
                                                )
                                            }
                                        }}
                                    />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {store.name}
                                        </p>
                                        <p className="text-xs text-base-content/50 truncate">
                                            {store.subdomain}
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={() =>
                            navigate(
                                '/registrations/product-categories'
                            )
                        }
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary"
                        disabled={
                            loading ||
                            !name.trim() ||
                            (isSubcategory && !parentId)
                        }
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : isSubcategory ? (
                            'Criar Subcategoria'
                        ) : (
                            'Criar Categoria'
                        )}
                    </button>
                </div>
            </form>

            <ConfirmModal
                isOpen={showNoStoreModal}
                onClose={() => setShowNoStoreModal(false)}
                onConfirm={saveCategory}
                loading={loading}
                title="Nenhuma loja selecionada"
                message={`Você não selecionou uma loja para ${isSubcategory ? 'esta subcategoria' : 'esta categoria'}, então ela não aparecerá em nenhuma loja online. Clique em Cancelar para escolher uma loja, ou em Criar sem loja para continuar.`}
                confirmButtonText="Criar sem loja"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-warning"
            />
        </div>
    )
}
